import React, { useCallback, useEffect, useRef, useState } from 'react';

type CheckStatus = 'idle' | 'checking' | 'pass' | 'warning' | 'fail';

const TEST_SENTENCE = 'The quick brown fox jumps over the lazy dog';

function isEdgeMacOS(): boolean {
  const ua = navigator.userAgent;
  return ua.includes('Edg/') && ua.includes('Mac OS');
}

function calculateMatch(expected: string, actual: string): number {
  if (!actual.trim()) return 0;
  const expectedWords = expected.toLowerCase().split(/\s+/);
  const actualWords = actual.toLowerCase().split(/\s+/);
  const matches = expectedWords.filter((word) =>
    actualWords.some((w) => w.includes(word) || word.includes(w))
  ).length;
  return Math.round((matches / expectedWords.length) * 100);
}

function getStatusDotClass(checkStatus: CheckStatus): string {
  switch (checkStatus) {
    case 'pass':
      return 'check-dot green';
    case 'warning':
      return 'check-dot yellow';
    case 'fail':
      return 'check-dot red';
    case 'checking':
      return 'check-dot checking';
    default:
      return 'check-dot idle';
  }
}

const SystemReadinessChecks: React.FC = () => {
  const [noiseCheckStatus, setNoiseCheckStatus] = useState<CheckStatus>('idle');
  const [noiseCheckMessage, setNoiseCheckMessage] = useState('Click to test');
  const [noiseCheckLevels, setNoiseCheckLevels] = useState<number[]>(new Array(24).fill(0));
  const [noiseCheckDb, setNoiseCheckDb] = useState(-100);

  const [transcriptionCheckStatus, setTranscriptionCheckStatus] = useState<CheckStatus>('idle');
  const [transcriptionCheckMessage, setTranscriptionCheckMessage] = useState('Click to test');
  const [transcriptionText, setTranscriptionText] = useState('');
  const [transcriptionMatch, setTranscriptionMatch] = useState(0);
  const [isTranscriptionListening, setIsTranscriptionListening] = useState(false);

  const [speedCheckStatus, setSpeedCheckStatus] = useState<CheckStatus>('idle');
  const [speedCheckMessage, setSpeedCheckMessage] = useState('Click to test');
  const [speedLatency, setSpeedLatency] = useState(0);

  const noiseCheckStreamRef = useRef<MediaStream | null>(null);
  const noiseCheckContextRef = useRef<AudioContext | null>(null);
  const noiseCheckAnalyserRef = useRef<AnalyserNode | null>(null);
  const noiseCheckAnimationRef = useRef<number | null>(null);
  const transcriptionRecognitionRef = useRef<{ stop: () => void } | null>(null);
  const transcriptionMatchRef = useRef(0);

  useEffect(() => {
    return () => {
      if (noiseCheckAnimationRef.current !== null) {
        cancelAnimationFrame(noiseCheckAnimationRef.current);
      }
      noiseCheckStreamRef.current?.getTracks().forEach((track) => track.stop());
      noiseCheckContextRef.current?.close().catch(() => {});
      try {
        transcriptionRecognitionRef.current?.stop();
      } catch {
        // ignore
      }
    };
  }, []);

  const startNoiseCheck = useCallback(async () => {
    setNoiseCheckStatus('checking');
    setNoiseCheckMessage('Accessing microphone...');

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      noiseCheckStreamRef.current = stream;

      const audioContext = new AudioContext();
      noiseCheckContextRef.current = audioContext;

      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.8;
      noiseCheckAnalyserRef.current = analyser;

      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);

      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      let checkDuration = 0;
      const totalCheckTime = 5000;

      const analyzeAudio = () => {
        if (!noiseCheckAnalyserRef.current) return;

        analyser.getByteFrequencyData(dataArray);
        const average = dataArray.reduce((a, b) => a + b, 0) / bufferLength;
        const dbLevel = average > 0 ? 20 * Math.log10(average / 255) : -100;

        const levels: number[] = [];
        const step = Math.floor(bufferLength / 24);
        for (let i = 0; i < 24; i += 1) {
          levels.push(dataArray[i * step] / 255);
        }

        setNoiseCheckLevels(levels);
        setNoiseCheckDb(dbLevel);
        setNoiseCheckMessage('Analyzing...');

        checkDuration += 50;

        if (checkDuration >= totalCheckTime) {
          let finalStatus: CheckStatus = 'pass';
          let finalMessage = 'Environment is quiet';

          if (dbLevel > -25) {
            finalStatus = 'fail';
            finalMessage = 'Too much noise';
          } else if (dbLevel > -40) {
            finalStatus = 'warning';
            finalMessage = 'Moderate noise';
          }

          setNoiseCheckStatus(finalStatus);
          setNoiseCheckMessage(finalMessage);
          stream.getTracks().forEach((track) => track.stop());
          noiseCheckStreamRef.current = null;
          return;
        }

        noiseCheckAnimationRef.current = requestAnimationFrame(analyzeAudio);
      };

      analyzeAudio();
    } catch (error) {
      console.error('Noise check error:', error);
      setNoiseCheckStatus('fail');
      setNoiseCheckMessage('Mic access denied');
    }
  }, []);

  const startTranscriptionTest = useCallback(() => {
    if (isEdgeMacOS()) {
      setTranscriptionCheckStatus('warning');
      setTranscriptionCheckMessage('Server transcription (Edge/macOS)');
      setTranscriptionMatch(100);
      return;
    }

    const SpeechRecognition =
      (window as unknown as { SpeechRecognition?: new () => any; webkitSpeechRecognition?: new () => any })
        .SpeechRecognition ||
      (window as unknown as { webkitSpeechRecognition?: new () => any }).webkitSpeechRecognition;

    if (!SpeechRecognition) {
      setTranscriptionCheckStatus('fail');
      setTranscriptionCheckMessage('Not supported');
      return;
    }

    setTranscriptionCheckStatus('checking');
    setTranscriptionText('');
    setTranscriptionMatch(0);
    setIsTranscriptionListening(true);
    setTranscriptionCheckMessage('Listening...');

    try {
      const recognition = new SpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = true;
      recognition.lang = 'en-US';
      transcriptionRecognitionRef.current = recognition;

      recognition.onresult = (event: { results: ArrayLike<{ 0: { transcript: string } }> }) => {
        let transcript = '';
        for (let i = 0; i < event.results.length; i += 1) {
          transcript += event.results[i][0].transcript;
        }
        const match = calculateMatch(TEST_SENTENCE, transcript);
        setTranscriptionText(transcript);
        setTranscriptionMatch(match);
        transcriptionMatchRef.current = match;
      };

      recognition.onend = () => {
        setIsTranscriptionListening(false);
        const match = transcriptionMatchRef.current;
        if (match >= 80) {
          setTranscriptionCheckStatus('pass');
          setTranscriptionCheckMessage('Working well');
        } else if (match >= 50) {
          setTranscriptionCheckStatus('warning');
          setTranscriptionCheckMessage('Partially working');
        } else {
          setTranscriptionCheckStatus('fail');
          setTranscriptionCheckMessage('Low accuracy');
        }
      };

      recognition.onerror = (event: { error: string }) => {
        setIsTranscriptionListening(false);
        if (event.error === 'network') {
          setTranscriptionCheckStatus('warning');
          setTranscriptionCheckMessage('Using server transcription');
          setTranscriptionMatch(100);
        } else {
          setTranscriptionCheckStatus('fail');
          setTranscriptionCheckMessage('Test failed');
        }
      };

      recognition.start();
      window.setTimeout(() => {
        try {
          recognition.stop();
        } catch {
          // ignore
        }
      }, 8000);
    } catch {
      setTranscriptionCheckStatus('fail');
      setIsTranscriptionListening(false);
      setTranscriptionCheckMessage('Failed to start');
    }
  }, []);

  const startSpeedTest = useCallback(async () => {
    setSpeedCheckStatus('checking');
    setSpeedCheckMessage('Testing...');

    try {
      const latencies: number[] = [];
      for (let i = 0; i < 3; i += 1) {
        const startTime = performance.now();
        await fetch(window.location.origin, { method: 'HEAD', cache: 'no-store' }).catch(() => {});
        latencies.push(performance.now() - startTime);
      }

      const avgLatency = Math.round(latencies.reduce((a, b) => a + b, 0) / latencies.length);
      setSpeedLatency(avgLatency);

      let finalStatus: CheckStatus = 'pass';
      let finalMessage = `Good (${avgLatency}ms)`;

      if (avgLatency > 300) {
        finalStatus = 'fail';
        finalMessage = `Slow (${avgLatency}ms)`;
      } else if (avgLatency > 100) {
        finalStatus = 'warning';
        finalMessage = `Moderate (${avgLatency}ms)`;
      }

      setSpeedCheckStatus(finalStatus);
      setSpeedCheckMessage(finalMessage);
    } catch {
      setSpeedCheckStatus('fail');
      setSpeedCheckMessage('Test failed');
    }
  }, []);

  return (
    <div className="readiness-checks-section">
      <div className="checks-header">
        <label className="input-label">System Checks</label>
        <button
          type="button"
          className="run-all-btn"
          onClick={async () => {
            await startSpeedTest();
            window.setTimeout(() => {
              void startNoiseCheck();
            }, 500);
          }}
          disabled={
            speedCheckStatus === 'checking' ||
            noiseCheckStatus === 'checking' ||
            isTranscriptionListening
          }
        >
          Run All
        </button>
      </div>

      <div className="checks-row-top">
        <div className="check-compact">
          <div className="check-compact-header">
            <span className={getStatusDotClass(speedCheckStatus)} />
            <span className="check-compact-title">Network</span>
            {speedLatency > 0 && <span className="check-compact-metric">{speedLatency}ms</span>}
          </div>
          <div className="check-compact-status">{speedCheckMessage || 'Test connection'}</div>
          <button
            type="button"
            className="check-compact-btn"
            onClick={() => void startSpeedTest()}
            disabled={speedCheckStatus === 'checking'}
          >
            {speedCheckStatus === 'checking' ? '...' : speedCheckStatus === 'idle' ? 'Test' : 'Retest'}
          </button>
        </div>

        <div className="checks-row-divider" />

        <div className="check-compact">
          <div className="check-compact-header">
            <span className={getStatusDotClass(noiseCheckStatus)} />
            <span className="check-compact-title">
              {noiseCheckStatus === 'fail' ? 'Too much noise' : 'Background Noise'}
            </span>
            {noiseCheckDb > -100 && (
              <span className="check-compact-metric">{noiseCheckDb.toFixed(0)}dB</span>
            )}
          </div>
          <div className="check-compact-waveform">
            {noiseCheckLevels.length > 0 ? (
              noiseCheckLevels.map((level, idx) => (
                <div
                  key={idx}
                  className="wave-bar-sm"
                  style={{ height: `${Math.max(level * 100, 10)}%` }}
                />
              ))
            ) : (
              <span className="check-compact-status">
                {noiseCheckMessage || 'Test ambient noise'}
              </span>
            )}
          </div>
          <button
            type="button"
            className="check-compact-btn"
            onClick={() => void startNoiseCheck()}
            disabled={noiseCheckStatus === 'checking'}
          >
            {noiseCheckStatus === 'checking' ? '...' : noiseCheckStatus === 'idle' ? 'Test' : 'Retest'}
          </button>
        </div>
      </div>

      <div className="checks-row-speech">
        <div className="speech-test-header">
          <span className={getStatusDotClass(transcriptionCheckStatus)} />
          <span className="speech-test-title">Speech Recognition Test</span>
          {transcriptionMatch > 0 && (
            <span className="speech-test-accuracy">{transcriptionMatch}%</span>
          )}
        </div>

        <div className="speech-test-content">
          <div className="speech-test-prompt">
            <span className="speech-label">Read aloud:</span>
            <span className="speech-sentence">&quot;{TEST_SENTENCE}&quot;</span>
          </div>

          {transcriptionText ? (
            <div className="speech-test-result">
              <span className="speech-label">You said:</span>
              <span className="speech-heard">&quot;{transcriptionText}&quot;</span>
            </div>
          ) : (
            <div className="speech-test-hint">
              {isTranscriptionListening
                ? 'Listening... speak now'
                : transcriptionCheckMessage || 'Click the button and read the sentence above'}
            </div>
          )}
        </div>

        <button
          type="button"
          className="speech-test-btn"
          onClick={startTranscriptionTest}
          disabled={isTranscriptionListening}
        >
          {isTranscriptionListening
            ? 'Listening...'
            : transcriptionCheckStatus === 'idle'
              ? 'Start Speaking'
              : 'Try Again'}
        </button>
      </div>
    </div>
  );
};

export default SystemReadinessChecks;
